import React, { useState, useEffect, useMemo } from "react";
import Modal from "react-bootstrap/Modal";

const API_BASE = "https://api.iriscrypt.com";
const API_KEY = "f87f030d-eaf4-4a39-9d5d-c9ffdb90ab43";

const FieldLabel = ({ children, required }) => (
  <div
    style={{
      color: "#6b7280",
      fontSize: 11,
      fontWeight: 700,
      letterSpacing: "0.06em",
      textTransform: "uppercase",
      marginBottom: 8,
    }}
  >
    {children}
    {required && <span style={{ color: "#ef4444", marginLeft: 4 }}>*</span>}
  </div>
);

const inputStyle = {
  width: "100%",
  backgroundColor: "#fff",
  border: "1.5px solid #e5e7eb",
  borderRadius: 10,
  padding: "10px 14px",
  fontSize: 14,
  color: "#111827",
  outline: "none",
  transition: "border-color 0.15s, box-shadow 0.15s",
};

const focusHandlers = {
  onFocus: (e) => {
    e.target.style.borderColor = "#ff5100";
    e.target.style.boxShadow = "0 0 0 3px rgba(255,81,0,0.1)";
  },
  onBlur: (e) => {
    e.target.style.borderColor = "#e5e7eb";
    e.target.style.boxShadow = "none";
  },
};

const EditProduct = ({
  domain,
  show,
  productId,
  user,
  client,
  handleClose,
  handleFormSubmit,
}) => {
  const [itemCode, setItemCode] = useState("");
  const [name, setName] = useState("");
  const [metadataFields, setMetadataFields] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const resetForm = () => {
    setItemCode("");
    setName("");
    setMetadataFields([]);
  };

  const metadataFromFields = useMemo(() => {
    const obj = {};
    if (name && String(name).trim()) obj.name = name;
    for (const f of metadataFields) {
      const k = String(f.key || "").trim();
      const v = String(f.value ?? "").trim();
      if (k && v) obj[k] = v;
    }
    return obj;
  }, [name, metadataFields]);

  useEffect(() => {
    if (!show) {
      resetForm();
      return;
    }
    const prefill = async () => {
      if (!productId) return;
      try {
        setIsLoading(true);
        const resp = await fetch(`${API_BASE}/item`, {
          headers: { "x-api-key": API_KEY },
        });
        const data = await resp.json();
        const found = Array.isArray(data)
          ? data.find((it) => String(it._id) === String(productId))
          : null;
        if (!found) return;

        setItemCode(found.code || "");
        const meta = found.metadata || {};
        setName(meta.name || "");
        const pairs = Object.entries(meta)
          .filter(([k]) => k !== "name")
          .map(([k, v]) => ({ key: k, value: v ?? "" }));
        setMetadataFields(pairs);
      } catch (e) {
        console.error("Failed to prefill product", e);
      } finally {
        setIsLoading(false);
      }
    };

    prefill();
  }, [show, productId]);

  const addMetadataField = () => {
    setMetadataFields((s) => [...s, { key: "", value: "" }]);
  };

  const removeMetadataField = (index) => {
    setMetadataFields((s) => s.filter((_, i) => i !== index));
  };

  const updateMetadataField = (index, keyOrValue, newValue) => {
    setMetadataFields((s) => {
      const next = [...s];
      next[index] = { ...next[index], [keyOrValue]: newValue };
      return next;
    });
  };

  const moveFieldUp = (index) => {
    if (index === 0) return;
    setMetadataFields((s) => {
      const next = [...s];
      [next[index - 1], next[index]] = [next[index], next[index - 1]];
      return next;
    });
  };

  const moveFieldDown = (index) => {
    setMetadataFields((s) => {
      if (index === s.length - 1) return s;
      const next = [...s];
      [next[index + 1], next[index]] = [next[index], next[index + 1]];
      return next;
    });
  };

  const handleSubmit = async () => {
    if (!productId) { alert("Missing product id."); return; }
    if (!itemCode || !name) {
      alert("Please fill in both Item Code and Name.");
      return;
    }

    const payload = {
      code: itemCode,
      metadata: metadataFromFields,
    };

    try {
      setIsLoading(true);
      const response = await fetch(`${API_BASE}/item/${productId}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "x-api-key": API_KEY,
        },
        body: JSON.stringify(payload),
      });

      const result = await response.json();
      if (response.ok && result?.success) {
        handleClose();
        handleFormSubmit();
        resetForm();
      } else {
        console.error("Edit failed", result);
        alert(response.status === 409 ? "An item with this code already exists!" : result?.detail || "Failed to update item.");
      }
    } catch (error) {
      console.error("Error updating item:", error);
      alert("An error occurred.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <style>{`
        .edit-product-modal .modal-content {
          background: #fff;
          border: 1px solid #e5e7eb;
          border-radius: 18px;
          box-shadow: 0 20px 60px rgba(0,0,0,0.12);
        }
        .edit-product-modal .modal-header {
          background: #fff;
          border-bottom: 1px solid #f3f4f6;
          border-radius: 18px 18px 0 0;
          padding: 20px 24px 16px;
        }
        .edit-product-modal .modal-body {
          background: #fff;
          padding: 20px 24px;
        }
        .edit-product-modal .modal-footer {
          background: #f9fafb;
          border-top: 1px solid #f3f4f6;
          border-radius: 0 0 18px 18px;
          padding: 16px 24px;
        }
        .add-meta-btn:hover {
          border-color: #ff5100 !important;
          color: #ff5100 !important;
          background: rgba(255,81,0,0.04) !important;
        }
      `}</style>

      <Modal
        show={show}
        onHide={handleClose}
        centered
        dialogClassName="edit-product-modal"
        style={{ backdropFilter: "blur(3px)" }}
      >
        <Modal.Header closeButton>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div
              style={{
                width: 34, height: 34, borderRadius: 9,
                background: "rgba(255,81,0,0.08)",
                border: "1.5px solid rgba(255,81,0,0.2)",
                display: "flex", alignItems: "center", justifyContent: "center",
              }}
            >
              <i className="fas fa-box" style={{ color: "#ff5100", fontSize: 13 }} />
            </div>
            <div>
              <div style={{ fontWeight: 800, fontSize: 16, color: "#111827" }}>Edit Product SKU</div>
              <div style={{ fontSize: 12, color: "#9ca3af", marginTop: 1 }}>Update SKU details and metadata</div>
            </div>
          </div>
        </Modal.Header>

        <Modal.Body>
          {isLoading ? (
            <div style={{ textAlign: "center", padding: "32px 0", color: "#9ca3af" }}>
              <i className="fas fa-spinner fa-spin" style={{ fontSize: 22, marginBottom: 10, display: "block" }} />
              <div style={{ fontSize: 13, fontWeight: 600 }}>Loading product…</div>
            </div>
          ) : (
            <>
              {/* Item Code */}
              <div>
                <FieldLabel required>Product SKU Code</FieldLabel>
                <input
                  type="text"
                  value={itemCode}
                  onChange={(e) => setItemCode(e.target.value)}
                  placeholder="e.g. SKU-00123"
                  style={inputStyle}
                  {...focusHandlers}
                />
              </div>

              {/* Name */}
              <div style={{ marginTop: 16 }}>
                <FieldLabel required>Name</FieldLabel>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g. Hand Sanitizer 500ml"
                  style={inputStyle}
                  {...focusHandlers}
                />
              </div>

              {/* Metadata fields */}
              {metadataFields.length > 0 && (
                <div style={{ marginTop: 16 }}>
                  <FieldLabel>SKU Metadata</FieldLabel>
                  <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                    {metadataFields.map((field, index) => (
                      <div key={index} style={{ display: "flex", gap: 8, alignItems: "center" }}>
                        <input
                          type="text"
                          value={field.key}
                          onChange={(e) => updateMetadataField(index, "key", e.target.value)}
                          placeholder="Key"
                          style={{ ...inputStyle, flex: 1 }}
                          {...focusHandlers}
                        />
                        <input
                          type="text"
                          value={field.value}
                          onChange={(e) => updateMetadataField(index, "value", e.target.value)}
                          placeholder="Value"
                          style={{ ...inputStyle, flex: 1 }}
                          {...focusHandlers}
                        />
                        <div style={{ display: "flex", flexDirection: "column", gap: 3, flexShrink: 0 }}>
                          <button
                            type="button"
                            onClick={() => moveFieldUp(index)}
                            disabled={index === 0}
                            style={{
                              width: 28, height: 28, borderRadius: 6, border: "1.5px solid #e5e7eb",
                              background: "#fff", cursor: index === 0 ? "not-allowed" : "pointer",
                              display: "flex", alignItems: "center", justifyContent: "center",
                              opacity: index === 0 ? 0.4 : 1, flexShrink: 0,
                            }}
                          >
                            <i className="fas fa-chevron-up" style={{ fontSize: 10, color: "#6b7280" }} />
                          </button>
                          <button
                            type="button"
                            onClick={() => moveFieldDown(index)}
                            disabled={index === metadataFields.length - 1}
                            style={{
                              width: 28, height: 28, borderRadius: 6, border: "1.5px solid #e5e7eb",
                              background: "#fff", cursor: index === metadataFields.length - 1 ? "not-allowed" : "pointer",
                              display: "flex", alignItems: "center", justifyContent: "center",
                              opacity: index === metadataFields.length - 1 ? 0.4 : 1, flexShrink: 0,
                            }}
                          >
                            <i className="fas fa-chevron-down" style={{ fontSize: 10, color: "#6b7280" }} />
                          </button>
                        </div>
                        <button
                          type="button"
                          onClick={() => removeMetadataField(index)}
                          style={{
                            width: 32, height: 32, borderRadius: 8, border: "1.5px solid #fca5a5",
                            background: "#fef2f2", color: "#ef4444", cursor: "pointer",
                            display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
                          }}
                        >
                          <i className="fas fa-times" style={{ fontSize: 12 }} />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Add metadata button */}
              <button
                type="button"
                onClick={addMetadataField}
                className="add-meta-btn"
                style={{
                  marginTop: metadataFields.length > 0 ? 10 : 16,
                  width: "100%",
                  padding: "9px 0",
                  borderRadius: 10,
                  border: "1.5px dashed #d1d5db",
                  background: "transparent",
                  color: "#9ca3af",
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 7,
                  transition: "border-color 0.15s, color 0.15s, background 0.15s",
                }}
              >
                <i className="fas fa-plus" style={{ fontSize: 11 }} />
                Add SKU metadata field
              </button>
            </>
          )}
        </Modal.Body>

        <Modal.Footer>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={isLoading}
            style={{
              width: "100%",
              padding: "11px 0",
              borderRadius: 10,
              border: "none",
              background: isLoading ? "#e5e7eb" : "linear-gradient(135deg, #c13d00 0%, #ff5100 100%)",
              color: isLoading ? "#9ca3af" : "#fff",
              fontWeight: 700,
              fontSize: 15,
              cursor: isLoading ? "not-allowed" : "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 9,
              transition: "background 0.15s",
            }}
          >
            {isLoading ? (
              <>
                <i className="fas fa-spinner fa-spin" style={{ fontSize: 13 }} />
                Saving…
              </>
            ) : (
              <>
                Save Changes
                <i className="fas fa-arrow-right" style={{ fontSize: 13 }} />
              </>
            )}
          </button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default EditProduct;
